/**
 * Controls when the dev/staging password gate is active (build-time env).
 *
 * Why this exists: `REACT_APP_ENABLE_DEV_ACCESS_GATE` alone was easy to forget in
 * `.env`, so the gate never ran while still pointing `REACT_APP_API_URL` at a
 * non-production API (e.g. api-vle.appening.xyz).
 *
 * Priority:
 * 1. `REACT_APP_ENABLE_DEV_ACCESS_GATE=false` → gate off (use for real production builds).
 * 2. `REACT_APP_ENABLE_DEV_ACCESS_GATE=true` → gate on.
 * 3. `NODE_ENV === 'development'` (`npm start`) → gate on.
 * 4. `REACT_APP_API_URL` hostname ≠ production API host → gate on (staging / preview APIs).
 * 5. Otherwise → gate off (typical production build against api.vlemobile.com).
 */

const PRODUCTION_API_HOST = "api.vlemobile.com";

function trimEnv(value: string | undefined): string | undefined {
  return value?.trim();
}

function apiHostnameFromEnv(): string | null {
  const raw = trimEnv(process.env.REACT_APP_API_URL);
  if (!raw) return null;
  try {
    return new URL(raw).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export function isDevAccessGateEnabled(): boolean {
  const explicit = trimEnv(process.env.REACT_APP_ENABLE_DEV_ACCESS_GATE);
  if (explicit === "false") {
    return false;
  }
  if (explicit === "true") {
    return true;
  }

  if (process.env.NODE_ENV === "development") {
    return true;
  }

  const host = apiHostnameFromEnv();
  if (!host) {
    return false;
  }

  return host !== PRODUCTION_API_HOST;
}
